import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FamilyAvatar } from './FamilyAvatar';

interface ScreenPublicData {
 client_hash: string;
 name: string;
 address: string | null;
 latitude: number | null;
 longitude: number | null;
 installation_photos: { filename: string; url: string }[];
 mo_family_hash: string;
 family_name: string | null;
 family_image_url: string | null;
}

/**
 * 비owner 도 볼 수 있는 스크린 상세 모달. 마켓 listing 의 스크린 카드, 모패밀리 상세
 * 의 스크린 탭 등에서 공통 사용. 민감 정보 (재생 중 영상, IP, 리모컨 등) 일절 미노출
 * — 그건 owner 전용 MoClient.tsx 디테일 모달에서.
 */
export const ScreenPublicModal: React.FC<{
 clientHash: string | null;
 onClose: () => void;
}> = ({ clientHash, onClose }) => {
 const navigate = useNavigate();
 const [data, setData] = useState<ScreenPublicData | null>(null);
 const [error, setError] = useState<string | null>(null);
 const [lightbox, setLightbox] = useState<string | null>(null);

 useEffect(() => {
  if (!clientHash) { setData(null); setError(null); return; }
  setData(null);
  setError(null);
  fetch(`/moclient-test/public-detail/${clientHash}`, { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    if (!j.success) { setError(j.error || '조회 실패'); return; }
    setData(j.data);
   })
   .catch(err => setError(err.message || '조회 실패'));
 }, [clientHash]);

 if (!clientHash) return null;

 return (
  <>
   <div
    className="fixed inset-0 bg-black bg-opacity-50 sm:flex sm:items-center sm:justify-center sm:p-4"
    style={{ zIndex: 9999 }}
   >
    <div className="bg-white p-4 w-full h-full overflow-y-auto sm:p-6 sm:max-w-3xl sm:h-auto sm:max-h-[90vh] sm:rounded">
     <div className="flex justify-between items-center mb-4">
      <h2 className="text-xl font-bold flex items-center gap-2">
       <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
       </svg>
       스크린 정보
      </h2>
      <button onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="닫기">
       <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
       </svg>
      </button>
     </div>

     {error && <div className="bg-red-50 text-red-700 p-3 text-sm">{error}</div>}
     {!data && !error && <div className="text-gray-500 py-8 text-center">로딩 중...</div>}

     {data && (
      <div className="space-y-4">
       {data.installation_photos.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
         {data.installation_photos.map(p => (
          <img
           key={p.filename}
           src={p.url}
           alt=""
           className="w-full aspect-square object-cover border border-gray-200 cursor-pointer"
           onClick={() => setLightbox(p.url)}
          />
         ))}
        </div>
       ) : (
        <div className="bg-gray-100 aspect-video flex items-center justify-center text-gray-400 text-sm">
         설치 사진이 없습니다
        </div>
       )}

       <div className="border-b pb-3">
        <label className="text-sm text-gray-600">위치</label>
        <p className="font-medium mt-1">
         {data.address ? data.address : <span className="text-gray-400">주소 미등록</span>}
        </p>
        {(data.latitude !== null && data.longitude !== null) && (
         <p className="text-xs text-gray-500 mt-1">
          좌표 {data.latitude.toFixed(5)}, {data.longitude.toFixed(5)}
         </p>
        )}
       </div>

       <div className="border-b pb-3">
        <label className="text-sm text-gray-600">스크린 이름</label>
        <p className="text-sm mt-1">{data.name || <span className="text-gray-400">미등록</span>}</p>
       </div>

       {data.family_name && (
        <div className="pb-3">
         <label className="text-sm text-gray-600">소속 모패밀리</label>
         <button
          type="button"
          onClick={() => { onClose(); navigate(`/mofamily/${data.mo_family_hash}`); }}
          className="mt-1 flex items-center gap-2 hover:text-blue-600 hover:underline"
         >
          <FamilyAvatar
           name={data.family_name}
           familyHash={data.mo_family_hash}
           imageUrl={data.family_image_url}
           size={24}
          />
          <span className="text-sm font-medium">{data.family_name}</span>
         </button>
        </div>
       )}
      </div>
     )}
    </div>
   </div>

   {lightbox && (
    <div
     className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 cursor-zoom-out"
     style={{ zIndex: 10000 }}
     onClick={() => setLightbox(null)}
    >
     <img src={lightbox} alt="" className="max-w-full max-h-full object-contain" />
    </div>
   )}
  </>
 );
};
